import { toast } from "@/components/common/Toast"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Typography } from "@/components/ui/typography"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { ManagePlanSection } from "@/features/upgrade/ManagePlanSection"
import {
  languageOptions,
  useTranslation,
  type LanguageKeys,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import type { User } from "@/types/user"
import { userDisplayName, userEmail, userInitials } from "@/utils/userIdentity"
import { useLayoutEffect, useRef } from "react"
import { SettingsList, SettingsListRow } from "../../components/SettingsList"
import { SettingsPage, SettingsSection } from "../../components/SettingsPage"
import { SettingsSkeleton } from "../../components/SettingsStates"

/** Who is signed in, named the way the sidebar's account menu names them,
 *  and their role. */
function ProfileDetails({ user }: { user: User }) {
  const { translate } = useTranslation()
  // A role only grants or limits anything once team management is on.
  const teamManagement = useFeatureFlag("TEAM_MANAGEMENT")
  const name = userDisplayName(user)
  const email = userEmail(user)

  return (
    <>
      <SettingsSection>
        <div className="flex items-center gap-4 px-3">
          <Avatar className="size-14">
            <AvatarFallback className="bg-primary/10 text-primary">
              <Typography as="large">{userInitials(name)}</Typography>
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-1">
            <Typography as="large" className="truncate">
              {name}
            </Typography>
            {email && email !== name && (
              <Typography as="muted" className="truncate">
                {email}
              </Typography>
            )}
          </div>
        </div>
      </SettingsSection>
      {teamManagement && user.role && (
        <SettingsSection>
          <SettingsList>
            <SettingsListRow
              label={translate("TEAM/ROLE_LABEL")}
              trailing={
                <Typography as="muted">
                  {translate(
                    user.role === "admin"
                      ? "TEAM/ROLE_ADMIN"
                      : "TEAM/ROLE_MEMBER",
                  )}
                </Typography>
              }
            />
          </SettingsList>
        </SettingsSection>
      )}
    </>
  )
}

/** The user's language: the app's, and their notification emails', so the
 *  choice is saved to the account as well. */
function LanguageSection() {
  const { translate, changeLanguage, currentLanguage } = useTranslation()
  // The page re-renders in the new language before the choice is saved to
  // the account, so the confirmation reads the new language's catalog.
  const translateRef = useRef(translate)
  useLayoutEffect(() => {
    translateRef.current = translate
  })

  function onLanguageChange(language: string) {
    changeLanguage(language as LanguageKeys, (result) => {
      // "unchanged": the same language, or its catalog failed (toasted).
      if (result === "saved")
        toast.success(
          translateRef.current("SETTINGS/PREFERENCES/LANGUAGE_SAVED"),
        )
      if (result === "switched")
        toast.error(
          translateRef.current("SETTINGS/PREFERENCES/LANGUAGE_NOT_SAVED"),
        )
    })
  }

  return (
    <SettingsSection
      title={translate("SETTINGS/LANGUAGE_TITLE")}
      description={translate("SETTINGS/LANGUAGE_DESCRIPTION")}
    >
      <Select value={currentLanguage} onValueChange={onLanguageChange}>
        <SelectTrigger
          aria-label={translate("SETTINGS/LANGUAGE_TITLE")}
          className="sm:max-w-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {languageOptions.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SettingsSection>
  )
}

/** Who is signed in, and their language. Name and email come from the
 *  sign-in, and there is no API to change them. */
export function ProfilePage() {
  const { translate } = useTranslation()
  const { user } = useUser()

  return (
    <SettingsPage
      title={translate("SETTINGS/PROFILE/TITLE")}
      description={translate("SETTINGS/PROFILE/DESCRIPTION")}
      notice={translate("SETTINGS/PROFILE/NOTICE")}
    >
      {user ? (
        <ProfileDetails user={user} />
      ) : (
        <SettingsSection>
          <SettingsSkeleton rows={2} />
        </SettingsSection>
      )}
      <LanguageSection />
      <ManagePlanSection />
    </SettingsPage>
  )
}
